import React, { useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

const Cart = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasFiredRef = useRef(false);

  const status = searchParams.get("status");
  const sessionId = searchParams.get("session_id");
  const isFree = searchParams.get("free") === "true";

  const isSuccess = status === "success";
  const isCancelled = status === "cancelled";

  // Fire GA4 for paid Stripe orders only
  useEffect(() => {
    if (!sessionId || !isSuccess || isFree || hasFiredRef.current) return;
    hasFiredRef.current = true;

    const sendPurchaseEvent = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/cart/fetch-session/${sessionId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-sh-auth": localStorage.getItem("token"),
            },
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        const session = data?.data;
        if (!session) return;

        window.gtag?.("event", "purchase", {
          transaction_id: session.transactionId,
          value: session.amount,
          currency: session.currency.toUpperCase(),
          items: session.items || [],
        });
      } catch (error) {
        console.error("GA4 purchase event error:", error);
      }
    };

    sendPurchaseEvent();
  }, [sessionId, isSuccess, isFree]);

  const config = isSuccess
    ? isFree
      ? {
          icon: (
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v13m0-13V6a4 4 0 00-4-4H5.45a4 4 0 00-3.9 2.93L1 8m11 0H1m11 0h10M7 21h10a2 2 0 002-2V8H5v11a2 2 0 002 2z" />
              </svg>
            </div>
          ),
          badge: { text: "Complimentary Order", color: "bg-green-100 text-green-700" },
          heading: "It's on us! 🎉",
          subheading: "Your complimentary order has been confirmed.",
          body: "Your cards have been unlocked and are ready to use. No payment was needed — enjoy!",
          primaryLabel: "View My Cards",
          primaryAction: () => navigate("/dashboard/print"),
          secondaryLabel: "Back to Home",
          secondaryAction: () => navigate("/"),
        }
      : {
          icon: (
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          ),
          badge: { text: "Payment Confirmed", color: "bg-green-100 text-green-700" },
          heading: "You're all set! 🎉",
          subheading: "Your payment was processed successfully.",
          body: "Your cards have been unlocked and are ready to use. A confirmation has been sent to your email.",
          primaryLabel: "View My Cards",
          primaryAction: () => navigate("/dashboard/print"),
          secondaryLabel: "Back to Home",
          secondaryAction: () => navigate("/"),
        }
    : isCancelled
    ? {
        icon: (
          <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        ),
        badge: { text: "Checkout Cancelled", color: "bg-yellow-100 text-yellow-700" },
        heading: "No worries — nothing was charged",
        subheading: "You cancelled the checkout before completing payment.",
        body: "Your cart is still saved. Head back whenever you're ready to complete your order.",
        primaryLabel: "Return to Cart",
        primaryAction: () => navigate("/dashboard/print", { state: { openCart: true } }),
        secondaryLabel: "Back to Home",
        secondaryAction: () => navigate("/"),
      }
    : {
        icon: (
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
        ),
        badge: { text: "Payment Failed", color: "bg-red-100 text-red-600" },
        heading: "Something went wrong",
        subheading: "Your payment could not be processed.",
        body: "Don't worry — you haven't been charged. Please try again or use a different payment method.",
        primaryLabel: "Try Again",
        primaryAction: () => navigate("/dashboard/print", { state: { openCart: true } }),
        secondaryLabel: "Back to Home",
        secondaryAction: () => navigate("/"),
      };

  return (
    <div className="min-h-[85vh] flex items-center justify-center px-4 bg-gray-50">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 w-full max-w-md p-8 text-center">
        {config.icon}

        {/* Badge */}
        <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full mb-4 ${config.badge.color}`}>
          {config.badge.text}
        </span>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">{config.heading}</h1>
        <p className="text-gray-500 text-sm font-medium mb-2">{config.subheading}</p>
        <p className="text-gray-400 text-sm mb-8">{config.body}</p>

        <div className="flex flex-col gap-3">
          <button
            onClick={config.primaryAction}
            className="w-full bg-black text-white py-3 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-colors"
          >
            {config.primaryLabel}
          </button>
          <button
            onClick={config.secondaryAction}
            className="w-full border border-gray-200 text-gray-600 py-3 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            {config.secondaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Cart;